import React from 'react';
import { Link } from 'react-router-dom';

// Apple-inspired color palette (matching LandingPage.jsx)
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray600: '#6e6e6e',
  gray700: '#636363',
  gray900: '#333333',
};

// Global font styles
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 500,
    letterSpacing: '-0.02em',
    color: '#000000',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
    color: '#333333',
  }
};

const Footer = ({ activePage = "" }) => {
  const linkStyle = (isActive) => ({
    color: isActive ? colors.accent : colors.gray600,
    fontWeight: 500,
    letterSpacing: '-0.01em',
    transition: 'color 0.2s ease'
  });

  return (
    <footer className="bg-white border-t mt-auto relative overflow-hidden" 
      style={{
        borderColor: colors.gray100,
        fontFamily: fontStyles.fontFamily,
        paddingTop: '120px',
        paddingBottom: '200px',
        minHeight: '600px',
        background: 'linear-gradient(to bottom, white, #f8faff)'
      }}>
      {/* Simple Vertical Lines for Content Distribution */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
        <div className="max-w-7xl mx-auto h-full relative" style={{ paddingLeft: 24, paddingRight: 24 }}>
          {/* Left vertical lines - matching static pages brightness */}
          <div className="absolute left-4 top-0 bottom-0 w-px" 
            style={{
              background: 'rgba(30, 64, 175, 0.18)',
              opacity: 0.8
            }} 
          />
          <div className="absolute left-8 top-0 bottom-0 w-px" 
            style={{
              background: 'rgba(30, 64, 175, 0.14)',
              opacity: 0.9
            }} 
          />
          {/* Right vertical lines - matching static pages brightness */}
          <div className="absolute right-4 top-0 bottom-0 w-px" 
            style={{
              background: 'rgba(30, 64, 175, 0.18)',
              opacity: 0.8
            }} 
          />
          <div className="absolute right-8 top-0 bottom-0 w-px" 
            style={{
              background: 'rgba(30, 64, 175, 0.14)',
              opacity: 0.9
            }} 
          />
        </div>
      </div>
      <div className="container mx-auto px-6 max-w-6xl">
        {/* Main footer content */}
        <div className="flex flex-col md:flex-row justify-between mb-16 relative z-10">
          {/* Company info */}
          <div className="mb-12 md:mb-0 md:w-1/3">
            <div className="flex flex-col">
              <img src="/6.png" alt="SignPact Logo" width="160" height="50" className="h-14 mb-6" />
              <p className="text-base mb-8" style={{ color: colors.gray600, lineHeight: 1.7, maxWidth: "380px" }}>
                SignPact helps you create, manage, and sign contracts with AI-powered tools that simplify your workflow.
              </p>
              {/* Social Media Links */}
              <div className="flex space-x-6">
                <a href="https://www.linkedin.com/company/signpact/" target="_blank" rel="noopener noreferrer" 
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="LinkedIn">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"></path>
                  </svg>
                </a>
                <a href="https://github.com/signpact" target="_blank" rel="noopener noreferrer" 
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="GitHub">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd"></path>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:w-1/2 justify-end">
            {/* Product */}
            <div className="mb-12 md:mb-0 md:mr-20">
              <h3 className="text-base font-medium mb-6" style={{ color: colors.gray900 }}>Product</h3>
              <ul className="space-y-4">
                <li>
                  <a href="/#document-generation" className="text-base hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "features")}>
                    Features
                  </a>
                </li>
                <li>
                  <Link to="/pricing" className="text-base hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "pricing")}>
                    Pricing
                  </Link>
                </li>
                {/* <li>
                  <Link to="/plan-comparison" className="text-base hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "plan-comparison")}>
                    Feature Comparison
                  </Link>
                </li> */}
                <li>
                  <Link to="/vision" className="text-base hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "vision")}>
                    Vision
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-base font-medium mb-6" style={{ color: colors.gray900 }}>Legal</h3>
              <ul className="space-y-4">
                <li>
                  <Link to="/privacy" className="text-base hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "privacy")}>
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-base hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "terms")}>
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/compliance" 
                    className="text-base hover:text-blue-600 transition-colors" 
                    style={linkStyle(activePage === "compliance")}
                  >
                    Compliance
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer bottom - Much more prominent spacing like Pointer.so */}
        <div className="pt-8 pb-16 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center relative z-10">
          <p className="text-base mb-6 md:mb-0" style={{ color: colors.gray500 }}>
            © 2025 SignPact, Inc. All rights reserved.
          </p>
        </div>
      </div>
      
      {/* Background logo - simple version */}
      <div className="absolute bottom-0 left-0 w-full flex justify-center items-end" style={{
        zIndex: 0,
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none'
      }}>
        <div 
          style={{
            position: 'absolute',
            bottom: '-120px',
            width: 'min(82vw, 1300px)',
            minWidth: '600px',
            transform: 'translateX(-50%)',
            left: '50%',
          }}
        >
          <img 
            src="/6.png" 
            alt="" 
            style={{
              width: '100%',
              height: 'auto',
              opacity: 0.08,
              filter: 'grayscale(100%)',
              maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.9) 20%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0.2) 65%, rgba(0,0,0,0.05) 80%, rgba(0,0,0,0) 85%)',
              WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.9) 20%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0.2) 65%, rgba(0,0,0,0.05) 80%, rgba(0,0,0,0) 85%)',
              transition: 'all 0.6s ease'
            }}
          />
          
        </div>
      </div>
      
    </footer>
  );
};

export default Footer; 